/**
 * Conversaciones recientes del estudiante (pestañas del chat).
 *
 * Acuerdo con Santiago (reunión 01/09/2026, confirmado por WhatsApp a Carlos):
 * Galia conserva las ÚLTIMAS 3 conversaciones, accesibles como pestañas en la
 * parte superior del cuadro de chat (en lugar de los chips de sugerencias), con
 * vigencia de 2 SEMANAS. Las nuevas desplazan a las viejas.
 *
 * No hay almacenamiento nuevo: las conversaciones ya viven en Supabase
 * (`conversations.questions`, una fila por internal_interaction_id). Aquí solo
 * se eligen las que califican y se convierten al formato del chat. Retomar una
 * pestaña = rehidratar el historial y seguir escribiendo en la MISMA fila
 * (ver conversationSaverService.restoreConversationState), igual que hace el
 * resume automático < 24h de conversationResumeService.
 *
 * Solo se ofrecen conversaciones del MISMO modo que el actual: cargar un hilo
 * de "profe" dentro de "conversadora" (o uno de aide-prof en modo estudiante)
 * mezclaría prompts y comportamientos.
 */
import supabaseService from './supabaseService';
import { computeLastActivityMs, questionsToChatMessages } from './conversationResumeService';
import type { ChatMessage, Conversation } from '../types';

/**
 * Cantidad acordada: las últimas 3. Es el ÚNICO criterio (Carlos, 07/09/2026):
 * no hay ventana de vigencia; la conversación sale de las pestañas solo cuando
 * la desplazan tres más recientes.
 */
export const RECENT_MAX = 3;

/** Modos cuyas conversaciones tiene sentido retomar desde una pestaña. */
const TAB_MODES = new Set(['conversacional', 'estudio', 'actividades-docente']);

/** Filas leídas de Supabase por consulta (sobra para elegir 3 de un modo). */
// Se traen las últimas N de cualquier modo y se filtra por modo en memoria; con
// 50 basta para que las 3 del modo actual no queden fuera aunque el estudiante
// alterne mucho de modo.
const FETCH_LIMIT = 50;

export interface RecentConversation {
  /** internal_interaction_id de la fila en Supabase. */
  id: string;
  mode: string;
  /** Última actividad (epoch ms): decide orden y caducidad. */
  lastActivityAt: number;
  /** Historial listo para webSocketService.loadChatHistory(). */
  messages: ChatMessage[];
  /** time_total ya acumulado en la fila (baseline para no pisarlo al retomar). */
  previousTimeTotalSeconds: number;
  /** Primer mensaje del estudiante, limpio y recortado, para titular la pestaña. */
  preview: string;
}

/** Quita el prefijo técnico `[text]` / `[audio]` con que se guardan los mensajes del usuario. */
function cleanUserText(content: string): string {
  return content.replace(/^\s*\[(text|audio)\]\s*/i, '').trim();
}

function buildPreview(messages: ChatMessage[], max = 42): string {
  const first = messages.find((m) => m.role === 'user' && cleanUserText(m.content));
  if (!first) return '';
  const text = cleanUserText(first.content).replace(/\s+/g, ' ');
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/**
 * Devuelve las últimas RECENT_MAX conversaciones del modo indicado, la más
 * reciente primero, sin límite de antigüedad. Falla abierto (lista vacía):
 * perder las pestañas es mejor que romper el chat.
 */
export async function getRecentConversations(
  email: string,
  mode: string | undefined,
): Promise<RecentConversation[]> {
  if (!email || !mode || !TAB_MODES.has(mode)) return [];
  try {
    const rows: Conversation[] = await supabaseService.getConversationsByUser(email, FETCH_LIMIT);
    const out: RecentConversation[] = [];
    for (const row of rows) {
      if (!row?.internal_interaction_id || row.mode !== mode) continue;
      const lastActivityAt = computeLastActivityMs(row);
      if (lastActivityAt === 0) continue;
      const messages = questionsToChatMessages(row.questions);
      // Sin un turno real del estudiante no hay nada que retomar.
      if (!messages.some((m) => m.role === 'user')) continue;
      out.push({
        id: row.internal_interaction_id,
        mode: row.mode,
        lastActivityAt,
        messages,
        previousTimeTotalSeconds:
          typeof row.time_total === 'number' && Number.isFinite(row.time_total) && row.time_total > 0
            ? Math.round(row.time_total)
            : 0,
        preview: buildPreview(messages),
      });
    }
    out.sort((a, b) => b.lastActivityAt - a.lastActivityAt);
    return out.slice(0, RECENT_MAX);
  } catch (err) {
    console.error('[RecentConversations] Error consultando conversaciones recientes:', err);
    return [];
  }
}

/**
 * Etiqueta corta de la pestaña según la fecha: "Hoy 10:30", "Ayer", "lun 1 sep".
 * Español para estudiantes, francés en modo profesor (misma regla que el resto
 * de la UI del aide-prof).
 */
export function formatTabLabel(lastActivityAt: number, lang: 'es' | 'fr', now = Date.now()): string {
  const d = new Date(lastActivityAt);
  const n = new Date(now);
  const sameDay = d.toDateString() === n.toDateString();
  const yesterday = new Date(n);
  yesterday.setDate(n.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const locale = lang === 'fr' ? 'fr-FR' : 'es-CO';
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `${lang === 'fr' ? "Aujourd'hui" : 'Hoy'} ${time}`;
  if (isYesterday) return lang === 'fr' ? 'Hier' : 'Ayer';
  return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' }).replace(/\.$/, '');
}
